// `shortlist-poc/shortlist-header/shortlist-header.tpl.html`, ported for the context that
// needs it first: every auth state's template renders
// `<shortlist-header hide-dynamic-ctas="::true">` above the page element, and it is 32px
// tall, so the ported screens were a whole header out of alignment without it.
//
// What is ported is the SHAPE the auth screens produce, read off production rather than
// inferred. At 200px, signed out, `hide-dynamic-ctas`:
//
//   <shortlist-header hide-dynamic-ctas>            display:flex, 32px
//     <div class="shortlist-header shortlist-slim-header-container">
//       <div class="shortlist-header-inner">
//         <div class="logo-wrap">
//           <span class="logo">WOO</span>
//           <span class="logo-product-type"></span>       ← EMPTY here
//         </div>
//         <div class="shortlist-header-ctas"></div>
//
// Three conditions decide that, and all three are ported rather than hardcoded:
//   - `currentViewMode` starts at `FlexMode` ($onInit) and the auth states never bind
//     `page-current-view-mode`, so the container class is the SLIM one. `FullMode` also
//     un-hides a literal " - My Hunters" suffix, which is why the class and the suffix move
//     together.
//   - `hideDynamicCtas` removes the actions dropdown and the pin CTA.
//   - the close button renders only `inIframe` AND only with an `onClose` — see the prop's
//     note. `inIframe` is false for a top-level tab and true inside the extension's frame, so
//     it is derived rather than assumed.
//
// The product name is genuinely empty on these screens: `initCurrentProduct()` has nothing
// to resolve before a session exists. It is a prop rather than a hardcoded '' so Phase 3 can
// pass Candidates / Contacts without touching this file.
import type { ReactElement, ReactNode } from 'react'
import { createElement } from 'react'
import { ViewMode } from '@woo-ext/protocol'

export interface ShortlistHeaderProps {
  /** `ViewMode.FullMode` swaps the container class and shows the " - My Hunters" suffix. */
  viewMode?: ViewMode
  /** `extensionProductDisplayNames[currentProduct]` — 'Candidates' | 'Contacts', or empty. */
  productName?: string
  /** Upstream's `hide-dynamic-ctas`: the auth states that render a header pass `::true`. */
  hideDynamicCtas?: boolean
  /**
   * `!shortlistPinnedModeService.forcePinnedMode && …showPinIcon`.
   *
   * Defaults to true because that is what production shows on the extension states this was
   * measured against; the service that can answer properly arrives with the frame messaging.
   */
  showPin?: boolean
  /** `shortlistPinnedModeService.isPinnedMode` — picks the class and the title. */
  pinned?: boolean
  /** `ctrl.togglePinnedMode()`. */
  onTogglePin?: () => void
  /** `ctrl.onHeaderTooltipToggle()` — opens the actions tooltip. */
  onToggleActions?: () => void
  /**
   * The actions tooltip's contents, when it is open.
   *
   * Passed in rather than rendered here for the same reason `CandidateFiltersBar` takes its
   * menu: the items are routing decisions and product state, which belong to the route, and
   * this component draws chrome.
   */
  actionsMenu?: ReactNode
  /** `extension-preferences-tooltip`, when the Settings item has opened it. */
  preferencesPanel?: ReactNode
  /** `extensionFrameService.isInIframe()`. Defaults to the real answer for this document. */
  inIframe?: boolean
  /**
   * Closes the frame. Upstream sends `IFrameEventType.Close` to the parent through
   * `extensionMessageService`, which needs the messaging layer of Phase 6.3.
   *
   * ⚠️ The close CTA renders only when this is supplied, NOT merely when framed. Upstream
   * gates it on `inIframe` alone because upstream always has a handler; here, rendering the
   * X without one gives the user a visible button that does nothing — worse than no button,
   * because the frame looks broken rather than unfinished.
   */
  onClose?: () => void
}

export function ShortlistHeader({
  viewMode = ViewMode.FlexMode,
  productName = '',
  hideDynamicCtas = false,
  showPin = true,
  pinned = false,
  onTogglePin,
  onToggleActions,
  actionsMenu,
  preferencesPanel,
  inIframe = typeof window !== 'undefined' && window.self !== window.top,
  onClose,
}: ShortlistHeaderProps): ReactElement {
  const full = viewMode === ViewMode.FullMode

  return createElement(
    // The element, not just the class: `_shortlist-header.scss` opens with
    // `shortlist-header { display: flex; flex-direction: column; }` — the same host-element
    // pattern as every page component (see PageHost).
    'shortlist-header',
    null,
    <div
      className={`shortlist-header ${full ? 'shortlist-full-header-container' : 'shortlist-slim-header-container'}`}
    >
      <div className="shortlist-header-inner">
        <div className="logo-wrap">
          {/* ⚠️ THE `{' '}` IS UPSTREAM'S WHITESPACE AND IT RENDERS. The template has a
              newline between these two spans, which HTML collapses to a single space — so
              production reads "WOO Candidates". JSX strips whitespace between elements when
              it contains a newline, so without this it reads "WOOCandidates".

              It could not be seen until now: the auth screens are the only callers so far and
              they leave `productName` empty, so there was nothing on the right of the space
              for its absence to close up against. The extension chrome is the first screen
              where both spans have text. */}
          <span className="logo">WOO</span>{' '}
          <span className="logo-product-type">{productName}</span>
          {full ? <span> - My Hunters</span> : null}
        </div>
        <div className="shortlist-header-ctas">
          {/* The dynamic group — actions dropdown and pin — is what `hide-dynamic-ctas`
              removes. It was an empty div while only the auth screens rendered this, all of
              which pass `::true`; the extension chrome is the first caller that does not.

              ⚠️ THE CONTROLS RENDER WITHOUT HANDLERS HERE, unlike the close X above, and the
              difference is which state was photographed. Upstream's own `ng-if="::inIframe"`
              means a top-level capture has no close button at all, so declining to draw a
              dead one diverges from nothing. These two ARE in every reference shot of an
              extension state, so omitting them would be a real divergence — a 30px hole in
              the header — and the pixel gate is the whole point of this phase. The tooltip
              and the pinned-mode toggle both need the frame messaging of Phase 6.3; until
              then a click is inert rather than wrong. */}
          {hideDynamicCtas ? null : (
            <div className="shortlist-header-ctas-group shortlist-header-ctas-group-dyn">
              {/* `position: relative` is inline for the reason given on `actionsMenu`: upstream
                  positions this tooltip with uib's service (`tooltip-append-to-body="false"`,
                  so it is already a local sibling), and anchoring to this span reproduces
                  "bottom" declaratively. */}
              <span
                className="shortlist-header-cta shortlist-header-actions-dropdown-container"
                style={{ position: 'relative' }}
              >
                {/* ⚠️ THE TOOLTIP HANGS OFF THE TOGGLE, NOT OFF THE CONTAINER, which is where
                    upstream attaches it (`shortlist-header.tpl.html:12` — the
                    `uib-tooltip-template` attributes are on `.shortlist-header-actions-dropdown`
                    with `tooltip-append-to-body="false"`). Anchoring it to the container
                    instead put the menu 7px low, because `top: 100%` then measures the
                    container's padded height rather than the icon's: measured at 378px, prod
                    opens at y=28 and this opened at y=35. */}
                <div
                  className="shortlist-header-actions-dropdown"
                  role="button"
                  aria-label="Actions"
                  tabIndex={0}
                  style={{ position: 'relative' }}
                  onClick={onToggleActions}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    onToggleActions?.()
                  }}
                >
                  <i className="far fa-ellipsis-v" />
                  {actionsMenu === undefined || actionsMenu === null ? null : (
                    <div
                      className="shortlist-header-tooltip-wrap tooltip bottom"
                      style={{ top: '100%' }}
                      // ⚠️ THE MENU IS A CHILD OF THE TOGGLE HERE AND A SIBLING UPSTREAM, so a
                      // click on one of its items bubbles back into `onToggleActions` and
                      // reopens the menu the item just closed — "Switch view" navigated and
                      // left the dropdown standing. uib inserts the tooltip with
                      // `element.after()` (`tooltip-append-to-body="false"`) and positions it in
                      // pixels; this port nests it instead so that `top: 100%` measures the
                      // icon rather than the padded container (the 7px the note above
                      // describes). Stopping propagation here is what the sibling structure
                      // gives upstream for free, and it costs no element: an extra wrapper
                      // would be a box inside `.tooltip-inner` that production does not have.
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <div className="tooltip-arrow" />
                      <div className="tooltip-inner">{actionsMenu}</div>
                    </div>
                  )}
                </div>
                {/* ⚠️ A SIBLING OF THE ⋮, NOT A CHILD OF THE MENU, and the wrapper span is
                    upstream's own (`shortlist-header.tpl.html:25`). It sits behind
                    `ng-show="editingUserPreferences"` — rendered only when open here, which
                    is the same thing for the diff since `ng-show` leaves a zero-box element
                    and this leaves none. The panel itself is positioned by the ELEMENT
                    selector inside it; see `ExtensionPreferencesTooltip`. */}
                {preferencesPanel === undefined || preferencesPanel === null ? null : (
                  <span className="extension-preferences-tooltip-wrapper">{preferencesPanel}</span>
                )}
              </span>
              {showPin ? (
                <span
                  className={`shortlist-header-cta pin-cta ${pinned ? 'pinned' : 'un-pinned'}`}
                  title={pinned ? 'unpin' : 'pin'}
                >
                  {/* The click is on the ICON upstream, not the span. Keeping it there keeps
                      the hit area the same shape. */}
                  <i
                    className="fas fa-thumbtack"
                    aria-hidden="true"
                    onClick={onTogglePin}
                  />
                </span>
              ) : null}
            </div>
          )}
          {inIframe && onClose !== undefined ? (
            <div className="shortlist-header-ctas-group shortlist-header-ctas-group-const">
              {/* `role`/`tabIndex`/`aria-label` and the key handler are a deliberate,
                  PIXEL-NEUTRAL divergence: upstream's bare `<span ng-click>` cannot be
                  reached with Tab or fired with Enter, so a keyboard user inside the frame
                  has no way to dismiss it. None of these attributes render, so the
                  visual-diff gate is unaffected — unlike a change of element, which would
                  bring the browser's own button styling with it. */}
              <span
                className="shortlist-header-cta"
                title="Close"
                role="button"
                aria-label="Close"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onClose()
                  }
                }}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
  )
}
